import { useCallback, useEffect, useState } from 'react';
import { AppDialog } from '../../components/AppDialog';
import { AppDataGrid, type AppColumn } from '../../components/AppDataGrid';
import { ListBox } from '../../components/ListBox';
import { useNotify } from '../../components/notifications';
import { useSaveFlow } from '../../components/confirm';
import { DateRangeSelect } from '../modules/DateRangeSelect';
import { copyReagentPrep, fetchReagentPreps } from './api';
import {
  PREP_SELECT_TYPE, REAGENT_RANGES, rangeCode, rangeDays,
  type ReagentDateFilter, type ReagentPrepListRow,
} from './types';

// ─────────────────────────────────────────────────────────────────────────────
// The Classify popup — what XAF's **New** action opens, from the Reagent Prep
// Log list AND from any of the three detail views
// (ReagentPreparationLogViewController.NewObjectAction_Executing :3806, which
// cancels the default new-object behaviour and shows the "ReagentPrepClassify"
// dashboard instead).  Spec §8.
//
// Two tabs:
//   1. "New Prep"      — pick a category, then start an empty prep of that type.
//   2. "Copy Previous" — pick ONE earlier prep; it is cloned under a fresh LLT.
//
// Legacy is 770×650 with a tab control; reproduced here on the shared
// <AppDialog> so it inherits Esc-to-close, Enter-activates-primary, Ctrl+S,
// focus-on-open and focus-return-to-opener for free (standing rule 14).
// ─────────────────────────────────────────────────────────────────────────────

/** Spec §8 tab 1 — "Select a Prep Type", in legacy's order. XAF renders it as a
 *  LIST BOX (LDM EnumListPropertyEditor → ASPxListBox, full width, 150px,
 *  caption on top), not radio buttons — users asked for the list (2026-10-16). */
const PREP_TYPES = [0, 1, 2] as const;

/** Spec §8 tab 2 — exactly the four columns legacy shows, at its widths, sorted
 *  DatePrepared descending. Nothing added: the earlier build carried an extra
 *  "Category" column, which XAF does not have. */
const COPY_COLUMNS: AppColumn<ReagentPrepListRow>[] = [
  { field: 'llt',          header: 'LLT',          width: 48 },
  { field: 'prepType',     header: 'PrepType',     width: 70 },
  { field: 'prepName',     header: 'PrepName',     width: 76 },
  // Date only, as G5's Copy Previous grid (owner bug list #7, 2026-11-03).
  { field: 'datePrepared', header: 'DatePrepared', width: 70, type: 'date' },
];

interface Props {
  /** Chosen a category on the "New Prep" tab — open an empty prep of that type. */
  onNewPrep: (prepSelectType: number) => void;
  /** Cloned an existing prep — open the clone (already saved, LLT minted). */
  onCopied: (newId: string) => void;
  onClose: () => void;
}

export function ClassifyDialog({ onNewPrep, onCopied, onClose }: Props) {
  const notify = useNotify();
  const runSave = useSaveFlow();
  const [tab, setTab] = useState<'new' | 'copy'>('new');
  const [prepType, setPrepType] = useState<number>(0);

  const [rows, setRows]       = useState<ReagentPrepListRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [picked, setPicked]   = useState<string | null>(null);
  // Legacy's Copy Previous tab defaults to 3M regardless of the tenant default
  // that scopes the main list (spec §8: "The filter defaults to 3M").
  const [dateFilter, setDateFilter] = useState<ReagentDateFilter>('3M');

  const loadCopy = useCallback(async (filter: ReagentDateFilter) => {
    setLoading(true);
    try {
      setRows((await fetchReagentPreps(filter)).rows);
    } catch (e) {
      notify.error(e instanceof Error ? e.message : 'Failed to load previous preparations.');
    } finally {
      setLoading(false);
    }
  }, [notify]);

  // Only fetch when the tab is actually opened — the list is thousands of rows
  // and most users never leave the "New Prep" tab.
  useEffect(() => { if (tab === 'copy' && rows.length === 0) void loadCopy(dateFilter); },
            [tab, rows.length, dateFilter, loadCopy]);

  /** `typeOverride`: the type a double-click / Enter just picked — passed in
   *  because the state update would not have landed yet. */
  async function accept(typeOverride?: number) {
    if (tab === 'new') { onNewPrep(typeOverride ?? prepType); return; }

    if (!picked) {
      // Spec §18 message 6, verbatim.
      notify.warning('Please select an item to proceed further.');
      return;
    }
    // A copy CREATES a record, so it runs through the shared save flow (standing
    // rule 7, 2026-11-03): the centred confirm first, the blocking loader while it
    // runs (a double-click can no longer make two copies), then "Copied
    // successfully." — WITHOUT the new LLT, since success messages never echo a new
    // ID; the clone opens straight after, showing it. Create is reason-exempt.
    const source = rows.find((r) => r.oid === picked)?.llt;
    let newId = '';
    await runSave({
      formName: 'Reagent Prep Log',
      auditAction: 'create',
      confirmTitle: 'Copy',
      confirmMessage: `Copy preparation ${source ?? ''} to a new LLT?`,
      save: async () => { newId = (await copyReagentPrep(picked)).oid; },
      successMessage: 'Copied successfully.',
      onSaved: () => { if (newId) onCopied(newId); },
      onError: (e) => notify.error(e instanceof Error ? e.message : 'Failed to copy the preparation.'),
    });
  }

  return (
    <AppDialog
      open
      // G5 titles this popup "Classify" (owner bug list #6, 2026-11-03).
      title="Classify"
      width={880}
      className="dlg-center"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="ca-btn ca-btn-solid" onClick={() => void accept()} title="OK">OK</button>
          <button type="button" className="ca-btn" onClick={onClose} title="Cancel">Cancel</button>
        </>
      }
    >
      {/* Boxed tabs, styled as the detail page's "Components" tab (Department). */}
      <div className="rgp-tabs" role="tablist">
        <button type="button" role="tab" aria-selected={tab === 'new'}
                className={tab === 'new' ? 'on' : ''} onClick={() => setTab('new')}>
          New Prep
        </button>
        <button type="button" role="tab" aria-selected={tab === 'copy'}
                className={tab === 'copy' ? 'on' : ''} onClick={() => setTab('copy')}>
          Copy Previous
        </button>
      </div>

      {tab === 'new' ? (
        <div className="rgp-typelist">
          <div className="rgp-typelist-cap">Select a Prep Type</div>
          <ListBox
            ariaLabel="Select a Prep Type"
            options={PREP_TYPES.map((t) => ({ value: t, label: PREP_SELECT_TYPE[t] ?? '' }))}
            value={prepType}
            onChange={(t) => setPrepType(t)}
            // Enter / double-click on a type = OK, so the keyboard path is one key.
            onActivate={(t) => { setPrepType(t); void accept(t); }}
          />
        </div>
      ) : (
        <>
          <div className="rgp-dlg-toolbar">
            {/* The G5 "Date Filter [3M ▾]" combo, as on the Reagent Preparation lists. */}
            <DateRangeSelect
              variant="field" label="Date Filter" ranges={REAGENT_RANGES}
              value={rangeDays(dateFilter)}
              onChange={(d) => { const c = rangeCode(d); setDateFilter(c); void loadCopy(c); }}
            />
          </div>
          <AppDataGrid<ReagentPrepListRow>
            rows={rows}
            columns={COPY_COLUMNS}
            getRowId={(r) => r.oid}
            loading={loading}
            // Single-select: legacy's grid is single-select and OK requires
            // exactly one row (spec §8).
            selectedRowId={picked}
            accentSelection
            // A filter box under every column, so an analyst can find their LLT fast.
            filterRow
            onRowClick={(r) => setPicked(r.oid)}
            defaultPageSize={10}
            emptyText="No previous preparations in this date range."
          />
        </>
      )}
    </AppDialog>
  );
}
